// (c) 2026 Planet Q Productions. All Rights Reserved.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const quaylaActivities = [
  "Analyzing environment parameters...",
  "Constructing terrain geometry...",
  "Painting the sky and atmosphere...",
  "Sculpting character models...",
  "Designing enemy blueprints...",
  "Texturing obstacle surfaces...",
  "Calibrating lighting systems...",
  "Rendering scene details...",
  "Assembling the game world...",
  "Fine-tuning visual effects...",
  "Optimizing render pipeline...",
  "Adding final touches...",
];

const QuaylaLoader = ({ currentStep, totalSteps }) => {
  const [activityIdx, setActivityIdx] = useState(0);
  const [dots, setDots] = useState('');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const imgRef = useRef(null);
  const frameRef = useRef(0);
  const ttsAudioRef = useRef(null);
  const ttsFetchedRef = useRef(false);

  // Fetch Quayla TTS voice on mount — plays immediately when ready
  useEffect(() => {
    if (ttsFetchedRef.current) return;
    ttsFetchedRef.current = true;
    axios.post(`${API}/generate-quayla-speech`, {})
      .then(res => {
        if (res.data.audio_base64) {
          const audio = new Audio(`data:audio/mp3;base64,${res.data.audio_base64}`);
          audio.volume = 0.85;
          ttsAudioRef.current = audio;
          // Play immediately — video is muted so no conflict
          audio.play().catch(() => {});
        }
      })
      .catch(() => {});
    return () => {
      if (ttsAudioRef.current) {
        ttsAudioRef.current.pause();
        ttsAudioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setActivityIdx(prev => (prev + 1) % quaylaActivities.length);
    }, 2800);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(timer);
  }, []);

  // Load the Quayla avatar image
  useEffect(() => {
    const img = new Image();
    img.onload = () => { imgRef.current = img; };
    img.src = '/quayla-avatar.jpg';
  }, []);

  // Animated canvas scene
  const drawScene = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    const f = frameRef.current++;
    const img = imgRef.current;

    ctx.clearRect(0, 0, W, H);

    // Background: deep space galaxy
    const bgGrad = ctx.createRadialGradient(W * 0.5, H * 0.5, 0, W * 0.5, H * 0.5, W * 0.9);
    bgGrad.addColorStop(0, '#0d1b3e');
    bgGrad.addColorStop(0.3, '#090f24');
    bgGrad.addColorStop(0.7, '#050815');
    bgGrad.addColorStop(1, '#000000');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, W, H);

    // Nebula clouds (soft colored blobs)
    const nebulae = [
      { x: 0.2, y: 0.3, r: 0.25, color: [40, 0, 120] },
      { x: 0.75, y: 0.6, r: 0.2, color: [0, 40, 100] },
      { x: 0.5, y: 0.15, r: 0.18, color: [80, 0, 60] },
    ];
    nebulae.forEach(n => {
      const nx = n.x * W + Math.sin(f * 0.003 + n.x * 10) * 15;
      const ny = n.y * H + Math.cos(f * 0.004 + n.y * 10) * 10;
      const nGrad = ctx.createRadialGradient(nx, ny, 0, nx, ny, n.r * W);
      nGrad.addColorStop(0, `rgba(${n.color[0]}, ${n.color[1]}, ${n.color[2]}, 0.08)`);
      nGrad.addColorStop(0.5, `rgba(${n.color[0]}, ${n.color[1]}, ${n.color[2]}, 0.03)`);
      nGrad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = nGrad;
      ctx.fillRect(0, 0, W, H);
    });

    // Stars — dense starfield with twinkling
    for (let i = 0; i < 200; i++) {
      const seed = i * 137.508;
      const sx = (seed * 7.31) % W;
      const sy = (seed * 3.17) % H;
      const twinkle = Math.sin(f * 0.03 + seed) * 0.5 + 0.5;
      const sz = (i % 7 === 0) ? 2 : (i % 3 === 0) ? 1.2 : 0.6;
      const brightness = twinkle * (0.4 + (i % 5) * 0.12);
      // Mix of white, blue-white, and warm stars
      const starColors = ['255,255,255', '180,200,255', '255,220,180', '200,220,255'];
      const col = starColors[i % starColors.length];
      ctx.fillStyle = `rgba(${col}, ${brightness})`;
      ctx.beginPath(); ctx.arc(sx, sy, sz, 0, Math.PI * 2); ctx.fill();
      // Glow on bright stars
      if (sz > 1.5 && brightness > 0.5) {
        ctx.fillStyle = `rgba(${col}, ${brightness * 0.15})`;
        ctx.beginPath(); ctx.arc(sx, sy, sz * 3, 0, Math.PI * 2); ctx.fill();
      }
    }

    // Slow-moving shooting star (occasional)
    const shootCycle = (f % 400);
    if (shootCycle < 30) {
      const t = shootCycle / 30;
      const ssX = W * 0.1 + t * W * 0.6;
      const ssY = H * 0.05 + t * H * 0.25;
      const tailLen = 60 * (1 - t);
      ctx.strokeStyle = `rgba(255, 255, 255, ${(1 - t) * 0.6})`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(ssX, ssY);
      ctx.lineTo(ssX - tailLen, ssY - tailLen * 0.4);
      ctx.stroke();
      ctx.fillStyle = `rgba(255, 255, 255, ${(1 - t) * 0.9})`;
      ctx.beginPath(); ctx.arc(ssX, ssY, 2, 0, Math.PI * 2); ctx.fill();
    }

    // Floating blue particles
    for (let i = 0; i < 20; i++) {
      const seed = i * 137.5;
      const px = ((seed * 7.3 + f * 0.15 * (0.3 + (i % 5) * 0.15)) % W);
      const py = ((seed * 3.1 + f * 0.1 * (0.2 + (i % 3) * 0.1)) % H);
      const sz = 1 + (i % 3);
      const alpha = 0.12 + Math.sin(f * 0.02 + i) * 0.08;
      ctx.fillStyle = `rgba(0, ${150 + (i % 100)}, 255, ${alpha})`;
      ctx.beginPath(); ctx.arc(px, py, sz, 0, Math.PI * 2); ctx.fill();
    }

    // ── QUAYLA AVATAR (Video or Static Image) ──
    const avatarSize = Math.min(W * 0.28, 200);
    const avatarX = W * 0.22;
    const avatarY = H * 0.32;

    // Glow behind Quayla
    const glowGrad = ctx.createRadialGradient(avatarX, avatarY, avatarSize * 0.2, avatarX, avatarY, avatarSize * 0.9);
    glowGrad.addColorStop(0, `rgba(0, 100, 255, ${0.12 + Math.sin(f * 0.03) * 0.05})`);
    glowGrad.addColorStop(1, 'rgba(0, 50, 150, 0)');
    ctx.fillStyle = glowGrad;
    ctx.beginPath(); ctx.arc(avatarX, avatarY, avatarSize * 0.9, 0, Math.PI * 2); ctx.fill();

    // Static avatar with floating animation (voice plays from hidden video element)
    if (img) {
      const floatY = Math.sin(f * 0.025) * 4;
      const drawSize = avatarSize;

      ctx.save();
      ctx.beginPath();
      ctx.arc(avatarX, avatarY + floatY, drawSize * 0.45, 0, Math.PI * 2);
      ctx.clip();

      ctx.drawImage(img, avatarX - drawSize * 0.45, avatarY + floatY - drawSize * 0.45, drawSize * 0.9, drawSize * 0.9);

      // ── BLINKING EYES ──
      const blinkCycle = f % 120;
      const isBlinking = blinkCycle < 8;
      if (isBlinking) {
        const blinkProgress = blinkCycle < 4 ? blinkCycle / 4 : (8 - blinkCycle) / 4;
        const lidH = drawSize * 0.045 * blinkProgress;
        const eyeL_X = avatarX - drawSize * 0.12;
        const eyeY = avatarY + floatY - drawSize * 0.06;
        ctx.fillStyle = 'rgba(10, 30, 80, 0.85)';
        ctx.fillRect(eyeL_X - drawSize * 0.06, eyeY - lidH, drawSize * 0.12, lidH * 2);
        const eyeR_X = avatarX + drawSize * 0.12;
        ctx.fillRect(eyeR_X - drawSize * 0.06, eyeY - lidH, drawSize * 0.12, lidH * 2);
      }

      // ── TALKING MOUTH (synced while voice audio plays) ──
      const vid = videoRef.current;
      const ttsAudio = ttsAudioRef.current;
      const voicePlaying = (vid && !vid.ended && !vid.paused) || (ttsAudio && !ttsAudio.ended && !ttsAudio.paused);
      const talkSpeed = f * 0.25;
      const mouthOpenRaw = voicePlaying ? (Math.sin(talkSpeed) + Math.sin(talkSpeed * 1.7) * 0.5) * 0.5 + 0.5 : 0;
      const mouthOpen = mouthOpenRaw * drawSize * 0.07;
      const mouthX = avatarX;
      const mouthY = avatarY + floatY + drawSize * 0.17;
      ctx.fillStyle = `rgba(0, 20, 60, ${0.5 + mouthOpenRaw * 0.4})`;
      ctx.beginPath();
      ctx.ellipse(mouthX, mouthY, drawSize * 0.07, Math.max(drawSize * 0.01, mouthOpen), 0, 0, Math.PI * 2);
      ctx.fill();
      if (mouthOpen > drawSize * 0.02) {
        ctx.fillStyle = `rgba(0, 150, 255, ${0.3 + mouthOpenRaw * 0.3})`;
        ctx.beginPath();
        ctx.ellipse(mouthX, mouthY, drawSize * 0.04, mouthOpen * 0.6, 0, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();

      // Ring around avatar
      ctx.strokeStyle = `rgba(0, 200, 255, ${0.4 + Math.sin(f * 0.04) * 0.15})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(avatarX, avatarY + floatY, drawSize * 0.47, 0, Math.PI * 2);
      ctx.stroke();

      // Outer pulse ring
      const pulseR = drawSize * 0.47 + Math.sin(f * 0.05) * 8 + 10;
      ctx.strokeStyle = `rgba(0, 200, 255, ${0.1 + Math.sin(f * 0.05) * 0.05})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(avatarX, avatarY + floatY, pulseR, 0, Math.PI * 2);
      ctx.stroke();

      // ── BRAINSTORMING THOUGHT BUBBLES ──
      // Small glowing orbs float up from Quayla's head
      for (let b = 0; b < 5; b++) {
        const seed = b * 73.7;
        const bLife = ((f * 0.8 + seed) % 90) / 90; // 0→1 lifecycle
        if (bLife > 0.95) continue; // gap between spawns
        const bx = avatarX + Math.sin(seed + f * 0.02) * drawSize * 0.25;
        const by = avatarY + floatY - drawSize * 0.5 - bLife * drawSize * 0.6;
        const bSize = (2 + b % 3) * (1 - bLife * 0.5);
        const bAlpha = (1 - bLife) * 0.5;
        ctx.fillStyle = `rgba(0, 220, 255, ${bAlpha})`;
        ctx.beginPath(); ctx.arc(bx, by, bSize, 0, Math.PI * 2); ctx.fill();
        // Inner bright core
        ctx.fillStyle = `rgba(200, 255, 255, ${bAlpha * 0.6})`;
        ctx.beginPath(); ctx.arc(bx, by, bSize * 0.4, 0, Math.PI * 2); ctx.fill();
      }

      // Thought symbols (rotating around head)
      const symbols = ['?', '!', '*', '+'];
      for (let s = 0; s < symbols.length; s++) {
        const angle = (f * 0.015) + (s * Math.PI / 2);
        const orbitR = drawSize * 0.55;
        const sx = avatarX + Math.cos(angle) * orbitR;
        const sy = (avatarY + floatY - drawSize * 0.15) + Math.sin(angle) * orbitR * 0.35;
        const sAlpha = 0.15 + Math.sin(f * 0.04 + s * 2) * 0.1;
        ctx.font = `bold ${Math.min(14, drawSize * 0.09)}px Rajdhani, sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillStyle = `rgba(0, 200, 255, ${sAlpha})`;
        ctx.fillText(symbols[s], sx, sy);
      }
    }

    // ── HOLOGRAPHIC COMPUTER ──
    const hX = W * 0.6;                      // Hologram center X
    const hY = H * 0.35;                     // Hologram center Y
    const hW = Math.min(W * 0.35, 280);      // Hologram width
    const hH = hW * 0.7;                     // Hologram height

    // Hologram screen glow
    const hGlow = ctx.createRadialGradient(hX, hY, 0, hX, hY, hW * 0.7);
    hGlow.addColorStop(0, `rgba(0, 180, 255, ${0.06 + Math.sin(f * 0.02) * 0.02})`);
    hGlow.addColorStop(1, 'rgba(0, 80, 200, 0)');
    ctx.fillStyle = hGlow;
    ctx.fillRect(hX - hW * 0.7, hY - hH * 0.7, hW * 1.4, hH * 1.4);

    // Hologram frame (rounded rect with scan lines)
    ctx.save();
    ctx.globalAlpha = 0.7 + Math.sin(f * 0.03) * 0.1;
    const frameX = hX - hW / 2;
    const frameY = hY - hH / 2;
    const r = 8;

    // Frame fill
    ctx.fillStyle = 'rgba(0, 20, 50, 0.6)';
    ctx.beginPath();
    ctx.moveTo(frameX + r, frameY);
    ctx.lineTo(frameX + hW - r, frameY);
    ctx.quadraticCurveTo(frameX + hW, frameY, frameX + hW, frameY + r);
    ctx.lineTo(frameX + hW, frameY + hH - r);
    ctx.quadraticCurveTo(frameX + hW, frameY + hH, frameX + hW - r, frameY + hH);
    ctx.lineTo(frameX + r, frameY + hH);
    ctx.quadraticCurveTo(frameX, frameY + hH, frameX, frameY + hH - r);
    ctx.lineTo(frameX, frameY + r);
    ctx.quadraticCurveTo(frameX, frameY, frameX + r, frameY);
    ctx.fill();

    // Frame border
    ctx.strokeStyle = `rgba(0, 220, 255, ${0.5 + Math.sin(f * 0.04) * 0.15})`;
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Scan line effect
    for (let sy = frameY + 4; sy < frameY + hH; sy += 4) {
      ctx.fillStyle = `rgba(0, 200, 255, ${0.03 + Math.sin(sy * 0.5 + f * 0.1) * 0.02})`;
      ctx.fillRect(frameX + 2, sy, hW - 4, 1);
    }

    // Moving scan bar
    const scanY = frameY + ((f * 1.5) % hH);
    ctx.fillStyle = 'rgba(0, 255, 255, 0.08)';
    ctx.fillRect(frameX + 2, scanY, hW - 4, 3);

    ctx.restore();

    // ── TYPING TEXT ON HOLOGRAM ──
    const activity = quaylaActivities[Math.floor((f / 50)) % quaylaActivities.length];
    const visibleChars = Math.min(activity.length, Math.floor(f * 0.4) % (activity.length + 15));
    const typedText = activity.substring(0, visibleChars);

    ctx.font = `bold ${Math.min(14, hW * 0.05)}px 'Courier New', monospace`;
    ctx.fillStyle = `rgba(0, 255, 200, ${0.8 + Math.sin(f * 0.1) * 0.1})`;
    ctx.textAlign = 'left';

    // Code-like lines on the hologram
    const lineH = 16;
    const textX = frameX + 12;
    let textY = frameY + 22;

    // "BUILDING WORLD..." header
    ctx.fillStyle = 'rgba(0, 200, 255, 0.9)';
    ctx.font = `bold ${Math.min(13, hW * 0.047)}px 'Courier New', monospace`;
    ctx.fillText('> QUAYLA OS v3.7', textX, textY);
    textY += lineH;

    ctx.fillStyle = 'rgba(0, 255, 180, 0.7)';
    ctx.font = `${Math.min(11, hW * 0.04)}px 'Courier New', monospace`;
    ctx.fillText('> Initializing game engine...', textX, textY);
    textY += lineH;

    // Current activity with typing cursor
    ctx.fillStyle = 'rgba(0, 255, 220, 0.85)';
    ctx.fillText(`> ${typedText}`, textX, textY);
    // Blinking cursor
    if (f % 30 < 15) {
      const cursorX = textX + ctx.measureText(`> ${typedText}`).width + 2;
      ctx.fillStyle = 'rgba(0, 255, 200, 0.9)';
      ctx.fillRect(cursorX, textY - 10, 7, 12);
    }
    textY += lineH;

    // Progress line
    const pct = totalSteps > 0 ? Math.floor((currentStep / totalSteps) * 100) : 0;
    ctx.fillStyle = 'rgba(100, 200, 255, 0.6)';
    ctx.fillText(`> Progress: [${'\u2588'.repeat(Math.floor(pct / 8))}${'\u2591'.repeat(12 - Math.floor(pct / 8))}] ${pct}%`, textX, textY);
    textY += lineH;

    // Status lines
    const statuses = ['Environment', 'Character', 'Enemies'];
    statuses.forEach((label, i) => {
      const done = i < currentStep;
      const active = i === currentStep;
      ctx.fillStyle = done ? 'rgba(0, 255, 100, 0.7)' : active ? `rgba(0, 200, 255, ${0.5 + Math.sin(f * 0.1) * 0.3})` : 'rgba(100, 100, 150, 0.4)';
      ctx.fillText(`  ${done ? '[OK]' : active ? '[..]' : '[  ]'} ${label}`, textX, textY);
      textY += lineH;
    });

    // ── HOLOGRAPHIC KEYBOARD ──
    const kbY = frameY + hH + 12;
    const kbW = hW * 0.8;
    const kbX = hX - kbW / 2;
    const keySize = kbW / 12;
    const rows = 3;

    for (let row = 0; row < rows; row++) {
      const cols = 11 - row;
      const rowX = kbX + row * keySize * 0.4;
      for (let col = 0; col < cols; col++) {
        const kx = rowX + col * (keySize + 2);
        const ky = kbY + row * (keySize * 0.7 + 2);
        // Random key press effect
        const isPressed = Math.sin(f * 0.3 + row * 17 + col * 7.3) > 0.85;
        ctx.fillStyle = isPressed
          ? `rgba(0, 255, 200, ${0.35 + Math.sin(f * 0.2) * 0.1})`
          : `rgba(0, 120, 200, ${0.08 + Math.sin(f * 0.02 + col) * 0.03})`;
        ctx.fillRect(kx, ky, keySize - 1, keySize * 0.6);
        ctx.strokeStyle = `rgba(0, 180, 255, ${isPressed ? 0.5 : 0.15})`;
        ctx.lineWidth = 0.5;
        ctx.strokeRect(kx, ky, keySize - 1, keySize * 0.6);
      }
    }

    // ── CONNECTION LINE from Quayla to Hologram ──
    ctx.strokeStyle = `rgba(0, 200, 255, ${0.15 + Math.sin(f * 0.05) * 0.05})`;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 6]);
    ctx.beginPath();
    ctx.moveTo(avatarX + avatarSize * 0.4, avatarY);
    ctx.quadraticCurveTo(W * 0.42, hY - 20, frameX, hY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Data dots flowing along the connection
    for (let d = 0; d < 4; d++) {
      const t = ((f * 0.01 + d * 0.25) % 1);
      const dx = avatarX + avatarSize * 0.4 + (frameX - avatarX - avatarSize * 0.4) * t;
      const curveOff = Math.sin(t * Math.PI) * -20;
      const dy = avatarY + (hY - avatarY) * t + curveOff;
      ctx.fillStyle = `rgba(0, 255, 200, ${0.4 + Math.sin(f * 0.1 + d) * 0.2})`;
      ctx.beginPath(); ctx.arc(dx, dy, 2, 0, Math.PI * 2); ctx.fill();
    }

    // ── QUAYLA NAME LABEL ──
    ctx.font = `bold ${Math.min(16, W * 0.025)}px Rajdhani, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(0, 220, 255, 0.8)';
    ctx.fillText('QUAYLA', avatarX, avatarY + avatarSize * 0.55);
    ctx.font = `${Math.min(10, W * 0.016)}px Rajdhani, sans-serif`;
    ctx.fillStyle = 'rgba(0, 200, 255, 0.45)';
    ctx.fillText('AI WORLD BUILDER', avatarX, avatarY + avatarSize * 0.55 + 14);

    animRef.current = requestAnimationFrame(drawScene);
  }, [currentStep, totalSteps]);

  // Start/stop animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    const resize = () => {
      canvas.width = parent?.offsetWidth || window.innerWidth;
      canvas.height = parent?.offsetHeight || window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);
    frameRef.current = 0;
    animRef.current = requestAnimationFrame(drawScene);
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [drawScene]);

  const progress = totalSteps > 0 ? (currentStep / totalSteps) * 100 : 0;
  const stepLabels = ['Environment', 'Character', 'Enemies'];

  return (
    <div
      data-testid="quayla-loader"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center"
      style={{ background: '#000' }}
    >
      {/* Speaking video (hidden, audio muted - TTS voice plays independently) */}
      <video
        ref={videoRef}
        src="/quayla-speaking.mp4"
        playsInline
        autoPlay
        muted
        onError={() => {}}
        className="hidden"
        data-testid="quayla-video"
      />

      {/* Animated canvas scene (video plays inside avatar circle) */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        data-testid="quayla-canvas"
      />

      {/* Overlay UI (below canvas scene) */}
      <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center pb-8 z-10">
        {/* Step indicators */}
        <div className="flex gap-3 mb-4">
          {stepLabels.map((label, i) => (
            <div key={label} className="flex items-center gap-1.5">
              <div
                className="w-3 h-3 rounded-full border-2 transition-all duration-500"
                style={{
                  borderColor: i < currentStep ? '#00ff88' : i === currentStep ? '#00bfff' : '#333',
                  background: i < currentStep ? '#00ff88' : 'transparent',
                  boxShadow: i === currentStep ? '0 0 8px rgba(0,191,255,0.6)' : 'none',
                }}
              />
              <span
                className="text-xs uppercase tracking-wide"
                style={{
                  color: i < currentStep ? '#00ff88' : i === currentStep ? '#00bfff' : '#555',
                  fontFamily: "'Rajdhani', sans-serif",
                }}
              >
                {label}
              </span>
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <div className="w-64 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
          <div
            className="h-full rounded-full transition-all duration-1000 ease-out"
            style={{
              width: `${progress}%`,
              background: 'linear-gradient(90deg, #00bfff, #00ff88)',
              boxShadow: '0 0 10px rgba(0,191,255,0.5)',
            }}
          />
        </div>

        <p className="text-gray-500 text-xs mt-3 uppercase tracking-widest" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          Quayla is building your world{dots}
        </p>
      </div>
    </div>
  );
};

export default QuaylaLoader;
